import { Link, NavLink, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { Phone, Menu, X } from "lucide-react";
import { SITE } from "@/data/siteData";

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const links = [
    { to: "/", label: "Accueil" },
    { to: "/services", label: "Services" },
    { to: "/zones", label: "Zones d'intervention" },
    { to: "/contact", label: "Contact" },
  ];

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-40 bg-white transition-shadow ${scrolled ? "shadow-[0_8px_30px_rgb(0,54,126,0.06)]" : ""}`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-20">
        <Link to="/" data-testid="site-logo" className="flex items-center gap-3 group">
          <img
            src={SITE.logo}
            alt="Logo Dégraissage Hotte Melun"
            width="56"
            height="56"
            loading="eager"
            className="h-14 w-14 object-contain"
          />
          <div className="hidden sm:block leading-tight">
            <div className="font-extrabold text-[#00367e] text-lg" style={{ fontFamily: "Outfit" }}>
              Dégraissage Hotte
            </div>
            <div className="font-bold text-[#066ec4] text-sm tracking-wider uppercase">
              Melun · 77
            </div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `px-4 py-2 rounded-full font-semibold text-[15px] transition-colors ${
                  isActive ? "text-[#00367e] bg-[#f1f5fb]" : "text-slate-700 hover:text-[#00367e] hover:bg-slate-50"
                }`
              }
              end={l.to === "/"}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            href={`tel:${SITE.phoneTel}`}
            data-testid="header-call-btn"
            className="dh-cta-shine flex items-center gap-2 px-5 py-3 rounded-full bg-[#00367e] text-white font-bold hover:bg-[#066ec4] transition-colors shadow-lg"
          >
            <Phone size={18} /> {SITE.phoneDisplay}
          </a>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          aria-label="Menu"
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden p-2 rounded-full text-[#00367e] hover:bg-slate-100"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden border-t border-slate-100 bg-white">
          <div className="px-4 py-4 space-y-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                className={({ isActive }) =>
                  `block px-4 py-3 rounded-xl font-semibold ${
                    isActive ? "bg-[#00367e] text-white" : "text-[#00367e] hover:bg-slate-50"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <a
              href={`tel:${SITE.phoneTel}`}
              className="flex items-center justify-center gap-2 mt-3 px-5 py-3 rounded-full bg-[#00367e] text-white font-bold"
            >
              <Phone size={18} /> Appeler {SITE.phoneDisplay}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
